/**
 * Creates a unique array of items based on a key extractor function
 * More performant alternative to lodash's uniq/uniqBy
 *
 * @param items - Array of items to process
 * @param keyExtractor - Function to extract the unique key from each item
 * @param transformer - Optional function to transform each unique item
 * @returns Array of unique items (optionally transformed)
 */
export const uniqueByKey = <T extends Record<string, any>, K extends string | number, R = T>(
  items: T[] | undefined | null,
  keyExtractor: (item: T) => K,
  transformer?: (item: T) => R,
): R[] => {
  if (!items?.length) return [];

  const uniqueMap = new Map<K, R>();

  for (const item of items) {
    const key = keyExtractor(item);
    if (!uniqueMap.has(key)) {
      const result = transformer ? transformer(item) : (item as unknown as R);
      uniqueMap.set(key, result);
    }
  }

  return [...uniqueMap.values()];
};

/**
 * Creates a unique array of primitive values
 * More performant alternative to lodash's uniq
 * Normalizes null, undefined, and empty string values to empty string
 */
export const uniquePrimitives = <T>(items: T[] | undefined | null): T[] => {
  if (!items?.length) return [];

  // Normalize null, undefined, and empty string values to empty string
  const normalizedItems = items.map((item) => {
    if (item === null || item === undefined || item === '') {
      return '' as T;
    }
    return item;
  });

  return [...new Set(normalizedItems)];
};
